'use client';

import React from 'react';
import { User, Mail, Phone, CalendarDays, Package, CheckCircle, Heart, Clock, Edit } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useCurrentOrders } from '@/hooks/useOrders';
import { useWishlist } from '@/providers/WishlistProvider';
import { formatPrice, formatDate } from '@/lib/utils/format';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';

export function AccountOverviewTab() {
    const { user } = useAuth();
    const { orders, isLoading: ordersLoading } = useCurrentOrders();
    const { getWishlistCount } = useWishlist();
    const router = useRouter();
    const { buildPath } = useBasePath();

    if (!user) return null;

    // Calculate stats
    const totalOrders = orders?.length || 0;
    const deliveredOrders = orders?.filter(order => order.orderStatus === 'Delivered')?.length || 0;
    const inTransitOrders = orders?.filter(order =>
        order.orderStatus === 'Out for Delivery' ||
        order.orderStatus === 'Picking & Packing' ||
        order.orderStatus === 'In Fulfillment'
    )?.length || 0;
    const savedItemsCount = getWishlistCount();

    // Get recent orders (last 3)
    const recentOrders = orders?.slice(0, 3) || [];

    const stats = [
        {
            icon: Package,
            label: 'Total Orders',
            value: totalOrders,
            color: 'bg-hsecondary/80 text-[#fff]',
        },
        {
            icon: CheckCircle,
            label: 'Delivered',
            value: deliveredOrders,
            color: 'bg-hsecondary/80 text-[#fff]',
        },
        {
            icon: Heart,
            label: 'Saved Items',
            value: savedItemsCount,
            color: 'bg-hsecondary/80 text-[#fff]',
        },
        {
            icon: Clock,
            label: 'In Transit',
            value: inTransitOrders,
            color: 'bg-hsecondary/80 text-[#fff]',
        },
    ];

    return (
        <div className="pb-6 px-3 space-y-6 pb-24">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={index}
                            className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-center gap-3">
                                <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${stat.color}`}>
                                    <Icon className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-hsecondary">{stat.value}</p>
                                    <p className="text-sm text-slate-600">{stat.label}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Profile Information */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-hsecondary">Profile Information</h3>
                    <button
                        onClick={() => router.push(buildPath('/account/edit'))}
                        className="flex items-center gap-1 text-sm text-hprimary-600 hover:text-hprimary-700"
                    >
                        <Edit className="h-4 w-4" />
                        Edit
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hprimary-100">
                            <User className="h-5 w-5 text-hsecondary" />
                        </div>
                        <div>
                            <p className="text-xs text-hmuted">Full Name</p>
                            <p className="text-sm font-medium text-hsecondary">
                                {user.firstname} {user.lastname}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hprimary-100">
                            <Mail className="h-5 w-5 text-hsecondary" />
                        </div>
                        <div>
                            <p className="text-xs text-hmuted">E-mail Address</p>
                            <p className="text-sm font-medium text-hsecondary">{user.email}</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hprimary-100">
                            <Phone className="h-5 w-5 text-hsecondary" />
                        </div>
                        <div>
                            <p className="text-xs text-hmuted">Phone Number</p>
                            <p className="text-sm font-medium text-hsecondary">
                                {user.phoneNumber || 'Not provided'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hprimary-100">
                            <CalendarDays className="h-5 w-5 text-hsecondary" />
                        </div>
                        <div>
                            <p className="text-xs text-hmuted">Date of Birth</p>
                            <p className="text-sm font-medium text-hsecondary">
                                {user.dateOfBirth ? formatDate(user.dateOfBirth) : 'Not provided'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Orders */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-hsecondary">Recent Orders</h3>
                    <button
                        onClick={() => {
                            // Switch to orders tab
                            const event = new CustomEvent('switchTab', { detail: 'orders' });
                            window.dispatchEvent(event);
                        }}
                        className="text-sm text-hprimary-600 hover:text-hprimary-700"
                    >
                        View All →
                    </button>
                </div>
                {ordersLoading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div
                                key={index}
                                className="h-16 animate-pulse rounded-lg bg-slate-50"
                            />
                        ))}
                    </div>
                ) : recentOrders.length === 0 ? (
                    <p className="text-sm text-hmuted text-center py-8">No orders yet</p>
                ) : (
                    <div className="space-y-3">
                        {recentOrders.map((order) => (
                            <div
                                key={order._id}
                                onClick={() => router.push(buildPath(`/account/orders/${order._id}`))}
                                className="flex items-center justify-between rounded-lg bg-slate-50 p-4 hover:bg-slate-100 transition-colors cursor-pointer"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
                                        <Package className="h-5 w-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <div className='flex items-center gap-2'>
                                            <p className="text-sm font-medium text-hsecondary">
                                                ORD-{order._id?.slice(-6).toUpperCase()}
                                            </p>
                                            <div className='hidden sm:flex items-center gap-2'>
                                                <Badge className='p-1 text-xs'
                                                    variant={
                                                        order.orderStatus === 'Delivered' ? 'success' :
                                                            (order.orderStatus === 'Out for Delivery' || order.orderStatus === 'Picking & Packing' || order.orderStatus === 'In Fulfillment') ? 'primary' :
                                                                order.orderStatus === 'Pending' ? 'warning' : 'gray'
                                                    }>
                                                    {order.orderStatus}
                                                </Badge>
                                            </div>
                                        </div>
                                        <p className="text-xs text-hmuted">
                                            {formatDate(order.createdAt || new Date(), 'short')}
                                        </p>
                                        <div className='sm:hidden flex items-center gap-2 mt-2 w-fit'>
                                            <Badge className=' text-xs'
                                                variant={
                                                    order.orderStatus === 'Delivered' ? 'success' :
                                                        (order.orderStatus === 'Out for Delivery' || order.orderStatus === 'Picking & Packing' || order.orderStatus === 'In Fulfillment') ? 'primary' :
                                                            order.orderStatus === 'Pending' ? 'warning' : 'gray'
                                                }>
                                                {order.orderStatus}
                                            </Badge>
                                        </div>
                                    </div>

                                </div>
                                <div className="flex items-center gap-3">
                                    <p className="text-sm font-semibold text-hsecondary">
                                        {formatPrice(order.grandTotal || 0)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
